import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MailX } from "lucide-react";
import { MailWithoutDocument, MailWithoutDocumentReason, MailWithoutDocumentsResponse } from "types/InvoiceInbox";
import { fetchMailWithoutDocuments } from "services/invoice-inbox.service";
import { formatPrettyDate } from "utils/helperFunctions";
import { formatFileSize } from "./invoice-doc-files";

/**
 * Mail that reached the invoice mailbox but produced no document, with the
 * reason for each: the answer to "my invoice email never showed up".
 *
 * One quiet line above the register that opens into the list. It only reads;
 * a refused file is uploaded to the register by hand, as the hint says.
 *
 * Like the missing-invoices banner it renders nothing while loading, when
 * there is no such mail and when the list could not be fetched. A box that is
 * on screen on every visit would be looked past on the day it matters.
 */

type Props = {
  companyId: string;
  /** Bumped by the register whenever a document changes. */
  reloadToken: number;
};

// The server decides why a mail has no document; this only picks the words.
const WHY_TEXT_KEY: Record<MailWithoutDocumentReason, string> = {
  blocked: "invoice_inbox_mail_without_documents_why_blocked",
  quarantined: "invoice_inbox_mail_without_documents_why_quarantined",
  no_attachments: "invoice_inbox_mail_without_documents_why_no_attachments",
  auto_mail: "invoice_inbox_mail_without_documents_why_auto_mail",
  processing_failed: "invoice_inbox_mail_without_documents_why_processing_failed",
  attachments_rejected: "invoice_inbox_mail_without_documents_why_attachments_rejected",
};

// Built from the parts that exist: a mail without a usable From header has no
// address, and some senders have no display name.
function senderLabel(message: MailWithoutDocument, unknownSender: string): string {
  const name = message.fromName || "";
  const address = message.fromAddress || "";
  if (name && address) return `${name} <${address}>`;
  return name || address || unknownSender;
}

function MailRow({ message }: { message: MailWithoutDocument }) {
  const { t } = useTranslation();
  const sender = senderLabel(
    message,
    t("invoice_inbox_mail_without_documents_unknown_sender", { defaultValue: "(unknown sender)" })
  );
  const heldByRule = message.why === "blocked" || message.why === "quarantined";
  const rejected = message.rejectedAttachments;

  return (
    <li className="py-2">
      <div className="text-xs text-gray-500">
        {formatPrettyDate(message.receivedAt)} · {sender}
      </div>
      <div className="font-medium text-gray-900">
        {message.subject || t("invoice_inbox_mail_without_documents_no_subject", { defaultValue: "(no subject)" })}
      </div>
      <div>
        {t(WHY_TEXT_KEY[message.why])}
        {heldByRule && message.blockedReason && `: ${message.blockedReason}`}
      </div>
      {message.why === "attachments_rejected" &&
        (rejected === null ? (
          <div className="text-gray-500">
            {t("invoice_inbox_mail_without_documents_reason_unknown", {
              defaultValue: "The reason was not recorded (older mail)",
            })}
          </div>
        ) : (
          <ul className="ml-4 list-disc">
            {rejected.map((file, index) => (
              <li key={index}>
                <span className="font-medium">{file.fileName || "—"}</span>{" "}
                <span className="text-xs text-gray-500">{formatFileSize(file.size)}</span> —{" "}
                {t(`invoice_inbox_refusal_${file.reason}`, { defaultValue: file.reason })}
              </li>
            ))}
          </ul>
        ))}
    </li>
  );
}

export default function InvoiceInboxMailWithoutDocuments({ companyId, reloadToken }: Props) {
  const { t } = useTranslation();
  // Tagged with the company it was fetched for, so switching companies never
  // shows the previous one's mail while the next fetch runs, or fails.
  const [loaded, setLoaded] = useState<{ companyId: string; data: MailWithoutDocumentsResponse } | null>(null);
  const [expanded, setExpanded] = useState(false);
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestRef.current;
    try {
      const data = await fetchMailWithoutDocuments(companyId);
      // A slower earlier request must not overwrite a newer one.
      if (requestId !== requestRef.current) return;
      setLoaded({ companyId, data });
    } catch (err) {
      console.error("Failed to load mail without documents", err);
    }
  }, [companyId]);

  useEffect(() => {
    setExpanded(false);
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  const data = loaded && loaded.companyId === companyId ? loaded.data : null;
  if (!data || data.messages.length === 0) return null;

  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
      <div className="flex flex-wrap items-center gap-2">
        <MailX className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
        <span>
          {t("invoice_inbox_mail_without_documents_summary", {
            defaultValue: "Mail without a document in the last {{days}} days: {{count}}",
            days: data.windowDays,
            count: data.total,
          })}
        </span>
        <button
          type="button"
          aria-expanded={expanded}
          className="text-blue-600 hover:underline"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded
            ? t("invoice_inbox_mail_without_documents_hide", { defaultValue: "Hide" })
            : t("invoice_inbox_mail_without_documents_show", { defaultValue: "Show" })}
        </button>
      </div>
      {expanded && (
        <div className="mt-2 border-t border-gray-100 pt-1">
          <ul className="divide-y divide-gray-100">
            {data.messages.map((message) => (
              <MailRow key={message.id} message={message} />
            ))}
          </ul>
          {data.total > data.messages.length && (
            <p className="mt-1 text-xs text-gray-500">
              {t("invoice_inbox_mail_without_documents_shown_of_total", {
                defaultValue: "Showing the latest {{shown}} of {{total}}",
                shown: data.messages.length,
                total: data.total,
              })}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            {t("invoice_inbox_mail_without_documents_hint", {
              defaultValue: "A file that was not accepted can be uploaded to the register by hand.",
            })}
          </p>
        </div>
      )}
    </div>
  );
}
